<template>
  <div style="padding: 10px">
    <title-bar :title="$lang.common_txt293"></title-bar>
    <div v-if="noticeReadError" class="notice-diagnostic">
      {{ noticeReadError }}
    </div>
    <div v-if="noticeActionBlockedReason" class="notice-diagnostic">
      {{ noticeActionBlockedReason }}
    </div>

    <!-- 操作栏 -->
    <div v-if="noticeList.length > 0" class="d-flex-s" style="padding: 8px 5px">
      <p
        class="notice-action"
        :class="{ 'notice-action--disabled': noticeActionBlockedReason || noticeReadError }"
        style="color: #6f758e; font-size: var(--vk-fs-xs)"
        @click="readAll"
      >
        <van-icon name="browsing-history-o" size="14" />
        {{ $lang.mc_read_all || 'Read All' }}
      </p>
      <p
        class="notice-action"
        :class="{ 'notice-action--disabled': noticeActionBlockedReason || noticeReadError }"
        style="color: #6f758e; font-size: var(--vk-fs-xs)"
        @click="deleteRead"
      >
        <van-icon name="delete-o" size="14" />
        {{ $lang.mc_delete_read || 'Delete Read' }}
      </p>
    </div>

    <!-- 消息列表 -->
    <van-list
      v-model="loading"
      :finished="finished"
      :immediate-check="false"
      loading-text=" "
      @load="loadMore"
    >
      <div
        v-for="(item, index) in noticeList"
        :key="index"
        style="background: #2a2d3e; padding: 12px; margin: 0 0 10px; border-radius: 8px; position: relative"
        @click="viewDetail(item)"
      >
        <div class="d-flex-s">
          <div class="d-flex" style="align-items: center">
            <span v-if="!item.read" style="width: 8px; height: 8px; border-radius: 50%; background: #e8c03c; margin-right: 6px; flex-shrink: 0"></span>
            <p :style="{ color: item.read ? '#6f758e' : '#d2d6e2', fontSize: 'var(--vk-fs-base)', fontWeight: 'bold' }">{{ item.title }}</p>
          </div>
          <p style="color: #6f758e; font-size: var(--vk-fs-xs); flex-shrink: 0">{{ item.sendTime }}</p>
        </div>
        <p :style="{ color: item.read ? '#555' : '#9ca2b7', fontSize: 'var(--vk-fs-xs)', marginTop: '5px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }">{{ item.content }}</p>
      </div>
    </van-list>

    <van-empty
      v-if="noticeList.length === 0 && !loading"
      :image="require('../../../assets/img/common/img_no_data.png')"
      :description="$lang.noempt"
    />

    <!-- 详情弹窗 -->
    <van-popup
      v-model="showDetail"
      position="bottom"
      closeable
      style="background: #1a1d29; border-radius: 16px 16px 0 0; height: 70%"
    >
      <div style="padding: 20px 16px; overflow-y: auto; height: 100%" v-if="currentNotice">
        <h3 style="color: #fff; font-size: var(--vk-fs-md); font-weight: bold; margin-bottom: 6px">{{ currentNotice.title }}</h3>
        <p style="color: #6f758e; font-size: var(--vk-fs-xs); margin-bottom: 16px">{{ currentNotice.sendTime }}</p>
        <div style="color: #ccc; font-size: var(--vk-fs-base); line-height: 22px; word-break: break-word" v-safe-html.rich="currentNotice.content"></div>
      </div>
    </van-popup>
  </div>
</template>
<script>
import { NoticeList, NoticeDetails, NoticeReadAll, NoticeDeleteRead } from '@/api/common'
import { contentOf, failMessage, handleNeedLogin, isOk, isSafeWriteSuccess } from './safeFlow'

function firstValue(source, keys) {
  if (!source || typeof source !== 'object') return undefined
  for (const key of keys) {
    const value = source[key]
    if (value !== undefined && value !== null && value !== '') return value
  }
  return undefined
}

function readState(item) {
  const value = firstValue(item, ['read', 'readFlag', 'read_flag', 'isRead'])
  if (value === true) return true
  const token = String(value || '').toLowerCase()
  return token === 'y' || token === 'yes' || token === 'true' || token === '1'
}

function normalizeNoticeItem(item) {
  if (!item || typeof item !== 'object') return {}
  const noticeId = firstValue(item, ['noticeId', 'id', 'receiveId', 'msgId', 'msg_id'].concat(['notice_id', 'receive_id', 'messageId', 'message_id']))
  const msgId = firstValue(item, ['msgId', 'msg_id', 'noticeId', 'id'].concat(['messageId', 'message_id', 'notice_id', 'receiveId', 'receive_id']))
  return {
    ...item,
    noticeId,
    id: noticeId,
    msgId,
    msg_id: msgId,
    title: firstValue(item, ['title', 'msgTitle', 'msg_title'].concat(['subject', 'messageTitle', 'message_title'])) || '',
    content: firstValue(item, ['content', 'msgContent', 'msg_content', 'body'].concat(['message', 'messageContent', 'message_content'])) || '',
    sendTime: firstValue(item, ['sendTime', 'createDate', 'create_date', 'time'].concat(['createTime', 'create_time', 'send_time'])) || '',
    read: readState(item)
  }
}

function normalizeNoticeList(source) {
  const list = firstValue(source, ['noticeList', 'list', 'records', 'messageList', 'items', 'dataList'])
  return Array.isArray(list) ? list.map(normalizeNoticeItem).filter(item => item.noticeId || item.msgId || item.title || item.content) : []
}

function noticeRecordKey(item) {
  if (!item || typeof item !== 'object') return ''
  return [
    firstValue(item, ['noticeId', 'id', 'receiveId'].concat(['notice_id', 'receive_id', 'messageId', 'message_id'])),
    firstValue(item, ['msgId', 'msg_id'].concat(['messageId', 'message_id', 'notice_id'])),
    firstValue(item, ['sendTime', 'createDate', 'create_date'].concat(['createTime', 'create_time', 'send_time']))
  ].map(value => value === undefined || value === null ? '' : String(value)).join('|')
}

function mergeNoticeList(existing, incoming) {
  const merged = []
  const seen = new Set()
  ;[...(existing || []), ...(incoming || [])].forEach(item => {
    const key = noticeRecordKey(item)
    if (key && seen.has(key)) return
    if (key) seen.add(key)
    merged.push(item)
  })
  return merged
}

function normalizeNoticeDetail(source) {
  const detail = firstValue(source, ['detail', 'notice', 'message', 'content'])
  const candidate = detail && typeof detail === 'object' ? detail : source
  return normalizeNoticeItem(candidate)
}

function noticeDetailPayload(item) {
  const noticeId = firstValue(item, ['noticeId', 'id', 'receiveId', 'msgId', 'msg_id'].concat(['notice_id', 'receive_id', 'messageId', 'message_id']))
  const msgId = firstValue(item, ['msgId', 'msg_id', 'noticeId', 'id'].concat(['messageId', 'message_id', 'notice_id']))
  return {
    noticeId,
    msgId,
    id: noticeId,
    msg_id: msgId
  }
}

function noticeReadSnapshot(vm) {
  return {
    noticeList: vm.noticeList,
    pageIndex: vm.pageIndex,
    finished: vm.finished
  }
}

function hasPreservedNoticeState(snapshot) {
  return snapshot && (snapshot.pageIndex > 0 || snapshot.finished || (Array.isArray(snapshot.noticeList) && snapshot.noticeList.length > 0))
}

function restoreNoticeReadSnapshot(vm, snapshot) {
  if (!hasPreservedNoticeState(snapshot)) return false
  vm.noticeList = snapshot.noticeList
  vm.pageIndex = snapshot.pageIndex
  vm.finished = snapshot.finished
  return true
}

function noticeFailureMessage(error, fallback) {
  const message = failMessage(error, fallback)
  return /^network error$/i.test(String(message || '').trim()) ? fallback : message
}

const MINE_BLOCKED_MARKER_KEYS = ['failClosed', 'fail_closed', 'falseProof', 'false_proof', 'readOnlyAction', 'writeBlocked', 'backendBlocked', 'backendFailure', 'backend_failure', 'technicalMarker', 'technical_marker', 'mock', 'fallback']
const MINE_FALSE_PROOF_KEYS = ['writeProof', 'write_proof', 'affectedRowsProof', 'affected_rows_proof']
const MINE_STRICT_WRITE_PROOF_KEYS = ['writeProof', 'write_proof', 'serverProof', 'server_proof', 'affectedProof', 'affected_proof', 'affectedRowsProof', 'affected_rows_proof']
const MINE_WRITE_COUNT_PROOF_KEYS = ['affectedRows', 'affected_rows', 'affected', 'rowCount', 'row_count']
const MINE_FALSEY_PROOF_TOKENS = ['0', 'false', 'null', 'undefined', 'none', 'n/a', 'na', 'mock', 'placeholder', 'fallback', 'stub', 'blocked']
const MINE_BLOCKED_PROOF_TOKENS = ['backendfailure', 'backend_failure', 'technicalmarker', 'technical_marker']

function mineToken(value) {
  return String(value == null ? '' : value).trim().toLowerCase()
}

function hasMineBlockedWriteMarker(payload, depth = 0, seen = []) {
  if (!payload || typeof payload !== 'object' || depth > 6 || seen.includes(payload)) return false
  seen.push(payload)
  if (Array.isArray(payload)) return payload.some(item => hasMineBlockedWriteMarker(item, depth + 1, seen))
  return Object.keys(payload).some(key => {
    const value = payload[key]
    const token = String(value == null ? '' : value).trim().toLowerCase()
    if (MINE_BLOCKED_MARKER_KEYS.includes(key) && value !== undefined && value !== null && value !== '') return true
    if (MINE_FALSE_PROOF_KEYS.includes(key) && (value === false || value === 0 || ['false', '0', 'no', 'off', 'placeholder'].includes(token))) return true
    if (token === 'placeholder') return true
    return value && typeof value === 'object' && hasMineBlockedWriteMarker(value, depth + 1, seen)
  })
}

function hasPositiveMineWriteCount(value) {
  if (typeof value === 'number') return Number.isInteger(value) && value > 0
  if (typeof value !== 'string') return false
  const token = mineToken(value)
  return /^\d+$/.test(token) && !/^0+$/.test(token)
}

function hasStrictMineProofValue(value) {
  if (Array.isArray(value) || (value && typeof value === 'object')) return false
  if (value === true || value === false || value === 0) return false
  const token = mineToken(value)
  return !!token &&
    token !== 'true' &&
    !MINE_FALSEY_PROOF_TOKENS.includes(token) &&
    !token.includes('mock') &&
    !token.includes('placeholder') &&
    !token.includes('fallback') &&
    !token.includes('stub') &&
    !token.includes('read_only') &&
    !token.includes('read-only') &&
    !token.includes('read only') &&
    !token.includes('readonly') &&
    !token.includes('blocked') &&
    !MINE_BLOCKED_PROOF_TOKENS.some(marker => token.includes(marker)) &&
    !token.includes('unavailable') &&
    !token.includes('unimplemented') &&
    !token.includes('fail') &&
    !token.includes('error')
}

function hasStrictMineWriteProof(payload, depth = 0, seen = []) {
  if (!payload || typeof payload !== 'object' || depth > 6 || seen.includes(payload)) return false
  seen.push(payload)
  if (Array.isArray(payload)) return payload.some(item => hasStrictMineWriteProof(item, depth + 1, seen))
  return Object.keys(payload).some(key => {
    const value = payload[key]
    if (MINE_WRITE_COUNT_PROOF_KEYS.includes(key) && hasPositiveMineWriteCount(value)) return true
    if (MINE_STRICT_WRITE_PROOF_KEYS.includes(key) && hasStrictMineProofValue(value)) return true
    return value && typeof value === 'object' && hasStrictMineWriteProof(value, depth + 1, seen)
  })
}

function isMineSafeWriteSuccess(res) {
  return isSafeWriteSuccess(res) && hasStrictMineWriteProof(res) && !hasMineBlockedWriteMarker(res)
}

export default {
  name: 'email',
  data() {
    return {
      noticeList: [],
      loading: false,
      finished: false,
      pageIndex: 0,
      pageSize: 20,
      showDetail: false,
      currentNotice: null,
      noticeReadError: '',
      noticeActionBlockedReason: ''
    }
  },
  mounted() {
    this.fetchNoticeList(true)
  },
  methods: {
    async fetchNoticeList(reset = false) {
      const previousReadState = noticeReadSnapshot(this)
      if (reset) {
        this.pageIndex = 0
        this.finished = false
      }
      if (this.loading || this.finished) return
      this.loading = true
      try {
        const data = await NoticeList({
          messageType: 'systemNotice',
          pageIndex: this.pageIndex,
          pageSize: this.pageSize
        })
        if (handleNeedLogin(this, data, this.$lang.login_txt11 || this.$lang.Entrar || 'Login Now', { isValue: 'isLogin' }, { replace: true })) {
          this.finished = true
          return
        }
        if (isOk(data)) {
          this.noticeReadError = ''
          const list = normalizeNoticeList(contentOf(data))
          if (reset) {
            this.noticeList = list
          } else {
            this.noticeList = mergeNoticeList(this.noticeList, list)
          }
          this.finished = list.length < this.pageSize
          if (!this.finished) this.pageIndex += 1
        } else {
          this.finished = true
          this.noticeReadError = failMessage(data, 'This feature is temporarily unavailable. Please try again later.')
          restoreNoticeReadSnapshot(this, previousReadState)
          this.$toast({ message: this.noticeReadError, icon: 'cross' })
        }
      } catch (e) {
        this.finished = true
        this.noticeReadError = noticeFailureMessage(e, '当前消息数据读取失败，已保留已加载消息。')
        restoreNoticeReadSnapshot(this, previousReadState)
        this.$toast({ message: this.noticeReadError, icon: 'cross' })
      } finally {
        this.loading = false
      }
    },
    loadMore() {
      this.fetchNoticeList()
    },
    hasNoticeActionBlock() {
      const message = this.noticeActionBlockedReason || this.noticeReadError
      if (!message) return false
      this.$toast({ message, icon: 'cross' })
      return true
    },
    requiresNoticeLogin() {
      if (!('token' in this) || this.token) return false
      handleNeedLogin(this, { status: 'need_login' })
      return true
    },
    showCachedNoticeDetail(item) {
      if (this.currentNotice) return
      this.currentNotice = item
      this.showDetail = true
    },
    async viewDetail(item) {
      if (this.requiresNoticeLogin()) return
      // 先调详情接口（后端会标记已读）
      let detailLoaded = false
      try {
        const payload = noticeDetailPayload(item)
        const data = await NoticeDetails(payload)
        if (handleNeedLogin(this, data)) return
        if (isSafeWriteSuccess(data) && isMineSafeWriteSuccess(data)) {
          const detail = normalizeNoticeDetail(contentOf(data))
          this.currentNotice = {
            ...item,
            ...detail
          }
          detailLoaded = true
        } else {
          this.showCachedNoticeDetail(item)
          this.$toast({ message: failMessage(data, 'This feature is temporarily unavailable. Please try again later.'), icon: 'cross' })
        }
      } catch (e) {
        this.showCachedNoticeDetail(item)
        this.$toast({ message: noticeFailureMessage(e, '当前消息详情读取失败，已展示已加载内容。'), icon: 'cross' })
      }
      // 仅在详情接口确认成功后同步已读，避免接口失败时制造假已读状态。
      if (detailLoaded) {
        this.$set(item, 'read', true)
        this.showDetail = true
      }
    },
    async readAll() {
      if (this.requiresNoticeLogin()) return
      if (this.hasNoticeActionBlock()) return
      try {
        const data = await NoticeReadAll({ messageType: 'systemNotice' })
        if (handleNeedLogin(this, data)) return
        if (isSafeWriteSuccess(data) && isMineSafeWriteSuccess(data)) {
          this.noticeActionBlockedReason = ''
          this.noticeList.forEach(item => { this.$set(item, 'read', true) })
          this.$toast({ message: this.$lang.Sucesso || 'Success', icon: 'success' })
        } else {
          this.noticeActionBlockedReason = failMessage(data, 'This feature is temporarily unavailable. Please try again later.')
          this.$toast({ message: this.noticeActionBlockedReason, icon: 'cross' })
        }
      } catch (e) {
        this.noticeActionBlockedReason = noticeFailureMessage(e, 'This feature is temporarily unavailable. Please try again later.')
        this.$toast({ message: this.noticeActionBlockedReason, icon: 'cross' })
      }
    },
    async deleteRead() {
      if (this.requiresNoticeLogin()) return
      if (this.hasNoticeActionBlock()) return
      try {
        const data = await NoticeDeleteRead({ messageType: 'systemNotice' })
        if (handleNeedLogin(this, data)) return
        if (isSafeWriteSuccess(data) && isMineSafeWriteSuccess(data)) {
          this.noticeActionBlockedReason = ''
          this.noticeList = this.noticeList.filter(item => !item.read)
          this.$toast({ message: this.$lang.Sucesso || 'Success', icon: 'success' })
        } else {
          this.noticeActionBlockedReason = failMessage(data, 'This feature is temporarily unavailable. Please try again later.')
          this.$toast({ message: this.noticeActionBlockedReason, icon: 'cross' })
        }
      } catch (e) {
        this.noticeActionBlockedReason = noticeFailureMessage(e, 'This feature is temporarily unavailable. Please try again later.')
        this.$toast({ message: this.noticeActionBlockedReason, icon: 'cross' })
      }
    }
  }
}
</script>
